/* eslint-disable max-lines */
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useState } from 'react';
import P1 from '@base/P1/P1';
import RadioButton from '@base/RadioButton/RadioButton';
import TextField from '@base/TextField/TextField';
import AutoComplete from '@base/AutoComplete/AutoComplete';
import Dropdown from '@comp/Dropdown/Dropdown';
import { useDispatchToStore } from '@hooks/reduxHooks';
import { searchGoal, searchExam, searchTopology } from '@actions/educator';
import {
  fetchConcepts,
  toggleQuestionStatus,
  editOption,
  deleteLanguage
} from '@actions/question';
import Dialog from '@comp/Dialog/Dialog';
import { Spacings, BgColors, TextColors } from '@styles/index';
import { topologyLevels } from '@constants/index';
import {
  QUESTION_TYPES,
  QUESTION_TYPE_MAP,
  INTEGER_TYPES
} from '@constants/quiz';

const RadioWrapper = styled.div`
  display: flex;
  margin-bottom: ${Spacings.SPACING_6B};
  justify-content: space-between;
`;

const RadioParent = styled.div`
  display: flex;
  & > :not(:last-child) {
    margin-right: ${Spacings.SPACING_3B};
  }
`;

const Radio = styled(RadioButton)`
  padding-right: 0px;
  margin-right: ${Spacings.SPACING_2B};
  :hover {
    background-color: ${BgColors.GREY};
  }
`;

const RadioGroup = styled.div`
  display: flex;
  cursor: pointer;
  align-items: center;
`;

const InputWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${Spacings.SPACING_6B};
`;

const ConceptWrapper = styled(InputWrapper)`
  margin-bottom: ${Spacings.SPACING_3B};
`;

const Bar = styled.hr`
  margin-top: ${Spacings.SPACING_3B};
  margin-bottom: ${Spacings.SPACING_3B};
`;

const Input = styled(TextField)`
  &.MuiInputBase-root {
    width: 340px;
  }
`;

const AutoCompleted = styled(AutoComplete)`
  &.MuiAutocomplete-root {
    width: 340px;
  }
`;

const Topology = styled(P1)`
  width: fit-content;
  margin-left: auto;
  margin-bottom: ${Spacings.SPACING_6B};
  margin-right: ${Spacings.SPACING_1B};
`;

const boolOptions = [
  { value: 0, label: 'False' },
  { value: 1, label: 'True' }
];

const isActiveOptions = [
  { value: false, label: 'Inactive' },
  { value: true, label: 'Active' }
];
const questionTypes = [
  { value: 0, label: 'Fact Based' },
  { value: 1, label: 'Numerical Based' },
  { value: 2, label: 'Concept Based' }
];

const questionDifficulties = [
  { value: 1, label: 'Very Easy' },
  { value: 2, label: 'Easy' },
  { value: 3, label: 'Moderate' },
  { value: 4, label: 'Hard' },
  { value: 5, label: 'Very Hard' }
];

const searchResults = (id, setState, dispatch, parent) => (
  event,
  value,
  reason
) => {
  if (reason === 'reset') return;
  if (id === 'goal')
    dispatch(searchGoal(value)).then((res) => {
      if (res.result) setState(res.result);
    });
  else if (id === 'exam')
    dispatch(searchExam(value, parent.uid)).then((res) => {
      if (res.result) setState(res.result.results);
    });
  else if (id === 'concept')
    dispatch(fetchConcepts(value, parent.uid)).then((res) => {
      if (res.result) setState(res.result);
    });
  else if (id === 'subConcept')
    dispatch(
      searchTopology(
        value,
        `${topologyLevels.SUBCONCEPT},${topologyLevels.CONCEPT_FIELD}`,
        parent?.uid
      )
    ).then((res) => {
      if (res.result) setState(res.result.results);
    });
};

const createRadios = (options, onChange, label, value) => (
  <RadioWrapper>
    <P1>{label}</P1>
    <RadioParent>
      {options.map((option) => {
        const isChecked = value === option.value;
        return (
          <RadioGroup key={option.label}>
            <Radio
              onChange={() => onChange(option.value)}
              checked={isChecked}
              label={option.label}
            />
          </RadioGroup>
        );
      })}
    </RadioParent>
  </RadioWrapper>
);

const toggleStatus = (questionUID, dispatch) => (value) =>
  dispatch(toggleQuestionStatus(value, questionUID)).then((response) => {
    if (response.result)
      dispatch({
        type: 'SET_SNACKBAR_MESSAGE',
        message: 'Question change state successfull',
        messageType: 'success'
      });
  });

const handleValueChange = (id, dispatch) => (value) =>
  dispatch(editOption(id, value));

const handleAutoCompleteChange = (id, dispatch) => (event, value) =>
  dispatch(editOption(id, value));

const handleLanguageChange = (id, dispatch, setShowConfirm) => (
  event,
  value,
  reason
) => {
  if (reason === 'remove-option') setShowConfirm(true);
  else dispatch(editOption(id, value));
};

const removeLanguage = (
  language,
  setToBeRemoved,
  selectedLanguages,
  dispatch,
  closeModal
) => () => {
  dispatch(deleteLanguage(language));
  closeModal();
  setToBeRemoved(selectedLanguages?.[selectedLanguages.length - 1]);
};

const handleTextFieldChange = (id, dispatch) => (event) => {
  const year = parseInt(event.target.value, 10);
  if (year > new Date().getFullYear()) return;
  dispatch(editOption(id, year));
};

const renderIsActive = (isActive, questionUID, isEditForm, dispatch) => {
  if (isEditForm)
    return createRadios(
      isActiveOptions,
      toggleStatus(questionUID, dispatch),
      'Question is',
      isActive
    );
};

const renderDescriptiveQuestionType = (descriptiveQuestionType, dispatch) =>
  createRadios(
    questionTypes,
    handleValueChange('descriptiveQuestionType', dispatch),
    'Question Type',
    descriptiveQuestionType
  );

const renderQuestionDifficulty = (difficultyLevel, dispatch) =>
  createRadios(
    questionDifficulties,
    handleValueChange('difficultyLevel', dispatch),
    'Question Difficulty',
    difficultyLevel
  );

const renderExpectedTimeTaken = (expectedTime, dispatch) => (
  <InputWrapper>
    <P1>Expected Time to Solve (in secs)</P1>
    <Input
      autoComplete="off"
      type="number"
      placeholder="in secs"
      value={expectedTime}
      onChange={handleTextFieldChange('expectedTime', dispatch)}
    />
  </InputWrapper>
);

const getOptionLabel = (option) => option.name;

const getConceptName = (option) => `${option.parent.name} > ${option.name}`;

const getSubConceptName = (option) =>
  `${option.parent.parent.name} > ${option.parent.name} > ${option.name}`;

const getSelectedType = (questionType) =>
  QUESTION_TYPES.find((type) => type.value === questionType);

const renderGoalField = (goal, searchedGoals, setSearchedGoals, dispatch) => {
  return (
    <InputWrapper>
      <P1>Select goal</P1>
      <AutoCompleted
        placeholder="Search goal"
        autoComplete="off"
        value={goal}
        onChangeValue={searchResults('goal', setSearchedGoals, dispatch)}
        noOptionsText={null}
        getOptionLabel={getOptionLabel}
        disableOpenOnFocus
        disableClearable
        onOptionClick={handleAutoCompleteChange('goal', dispatch)}
        options={searchedGoals}
      />
    </InputWrapper>
  );
};

const renderQuestionType = (questionType, dispatch) => (
  <InputWrapper>
    <P1>Question Type</P1>
    <Dropdown
      options={QUESTION_TYPES}
      buttonWidth="340px"
      menuWidth="340px"
      menuColor={TextColors.BLUE}
      selectedOption={getSelectedType(questionType)}
      onOptionClick={({ value }) => {
        dispatch(editOption('questionType', value));
        dispatch(editOption('correctAnswer', []));
      }}
    />
  </InputWrapper>
);

const renderHasPassage = (hasPassage, dispatch) =>
  createRadios(
    boolOptions,
    handleValueChange('hasPassage', dispatch),
    'Has Passage',
    hasPassage
  );

const renderTypeOfInteger = (integerDataType, dispatch) =>
  createRadios(
    INTEGER_TYPES,
    handleValueChange('integerDataType', dispatch),
    'Type of data',
    integerDataType
  );

const renderExamField = (
  exam,
  searchedExams,
  goal,
  isPreviousYearQuestion,
  setSearchedExams,
  dispatch
) => {
  if (isPreviousYearQuestion)
    return (
      <InputWrapper>
        <P1>Previous exam name</P1>
        <AutoCompleted
          placeholder="Search exam"
          autoComplete="off"
          value={exam}
          onChangeValue={searchResults(
            'exam',
            setSearchedExams,
            dispatch,
            goal
          )}
          noOptionsText={null}
          getOptionLabel={getOptionLabel}
          disableOpenOnFocus
          disableClearable
          onOptionClick={handleAutoCompleteChange('exam', dispatch)}
          options={searchedExams}
        />
      </InputWrapper>
    );
};

const renderPrimaryConcept = (
  primaryConcept,
  searchedConcepts,
  goal,
  setSearchedConcepts,
  dispatch
) => {
  if (goal)
    return (
      <ConceptWrapper>
        <P1>Primary concept</P1>
        <div>
          <AutoCompleted
            placeholder="Search concept"
            autoComplete="off"
            fullWidth
            value={primaryConcept}
            onChangeValue={searchResults(
              'concept',
              setSearchedConcepts,
              dispatch,
              goal
            )}
            noOptionsText={null}
            getOptionLabel={getConceptName}
            disableOpenOnFocus
            disableClearable
            onOptionClick={handleAutoCompleteChange('primaryConcept', dispatch)}
            options={searchedConcepts}
          />
        </div>
      </ConceptWrapper>
    );
};

const renderPrimarySubConcept = (
  primarySubConcept,
  searchedSubConcepts,
  primaryConcept,
  setSearchedSubConcepts,
  dispatch
) => {
  if (primaryConcept)
    return (
      <ConceptWrapper>
        <P1 />
        <AutoCompleted
          placeholder="Search sub-concept"
          autoComplete="off"
          value={primarySubConcept}
          onChangeValue={searchResults(
            'subConcept',
            setSearchedSubConcepts,
            dispatch,
            primaryConcept
          )}
          noOptionsText={null}
          getOptionLabel={getSubConceptName}
          disableOpenOnFocus
          disableClearable
          onOptionClick={handleAutoCompleteChange(
            'primarySubConcept',
            dispatch
          )}
          options={searchedSubConcepts}
        />
      </ConceptWrapper>
    );
};

const renderSecondaryConcept = (
  secondaryConcept,
  searchedConcepts,
  goal,
  setSearchedConcepts,
  dispatch
) => {
  if (goal)
    return (
      <ConceptWrapper>
        <P1>Secondary concept</P1>
        <AutoCompleted
          placeholder="Search concept"
          autoComplete="off"
          value={secondaryConcept}
          onChangeValue={searchResults(
            'concept',
            setSearchedConcepts,
            dispatch,
            goal
          )}
          noOptionsText={null}
          getOptionLabel={getConceptName}
          disableOpenOnFocus
          disableClearable
          onOptionClick={handleAutoCompleteChange('secondaryConcept', dispatch)}
          options={searchedConcepts}
        />
      </ConceptWrapper>
    );
};

const renderSecondarySubConcept = (
  secondarySubConcept,
  searchedSubConcepts,
  secondaryConcept,
  setSearchedSubConcepts,
  dispatch
) => {
  if (secondaryConcept)
    return (
      <ConceptWrapper>
        <P1 />
        <AutoCompleted
          placeholder="Search sub-concept"
          autoComplete="off"
          value={secondarySubConcept}
          onChangeValue={searchResults(
            'subConcept',
            setSearchedSubConcepts,
            dispatch,
            secondaryConcept
          )}
          noOptionsText={null}
          getOptionLabel={getSubConceptName}
          disableOpenOnFocus
          disableClearable
          onOptionClick={handleAutoCompleteChange(
            'secondarySubConcept',
            dispatch
          )}
          options={searchedSubConcepts}
        />
      </ConceptWrapper>
    );
};

const renderExamYear = (examYear, isPreviousYearQuestion, dispatch) => {
  if (isPreviousYearQuestion)
    return (
      <InputWrapper>
        <P1>Previous exam year</P1>
        <Input
          autoComplete="off"
          type="number"
          placeholder="Exam year"
          value={examYear}
          onChange={handleTextFieldChange('examYear', dispatch)}
        />
      </InputWrapper>
    );
};

const renderPreviousYearQuestion = (isPreviousYearQuestion, goal, dispatch) => {
  if (goal)
    return createRadios(
      boolOptions,
      handleValueChange('isPreviousYearQuestion', dispatch),
      'Is previous year question',
      isPreviousYearQuestion
    );
};

const renderHasDiagram = (hasDiagram, dispatch) =>
  createRadios(
    boolOptions,
    handleValueChange('hasDiagram', dispatch),
    'Has Diagram',
    hasDiagram
  );

const renderHasEquation = (hasEquation, dispatch) =>
  createRadios(
    boolOptions,
    handleValueChange('hasEquation', dispatch),
    'Has equation',
    hasEquation
  );

const removeTag = (setShowConfirm, setToBeRemoved) => (option) => () => {
  setShowConfirm(true);
  setToBeRemoved(option);
};

const renderLanguageField = (
  selectedLanguages,
  allLanguages,
  setShowConfirm,
  setToBeRemoved,
  dispatch
) => {
  return (
    <InputWrapper>
      <P1>Select language(s)</P1>
      <AutoCompleted
        placeholder="Search language"
        autoComplete="off"
        multiple
        value={selectedLanguages}
        noOptionsText={null}
        getOptionLabel={getOptionLabel}
        disableOpenOnFocus
        disableClearable
        onOptionClick={handleLanguageChange(
          'selectedLanguages',
          dispatch,
          setShowConfirm
        )}
        options={allLanguages}
        removeTag={removeTag(setShowConfirm, setToBeRemoved)}
      />
    </InputWrapper>
  );
};

const Options = ({ data, isEditForm }) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [searchedGoals, setSearchedGoals] = useState([]);
  const [searchedExams, setSearchedExams] = useState([]);
  const [searchedConcepts, setSearchedConcepts] = useState([]);
  const [searchedSubConcepts, setSearchedSubConcepts] = useState([]);
  const closeModal = () => setShowConfirm(false);
  const {
    isActive,
    descriptiveQuestionType,
    difficultyLevel,
    expectedTime,
    goal,
    questionType,
    hasPassage,
    integerDataType,
    hasDiagram,
    isPreviousYearQuestion,
    examYear,
    exam,
    hasEquation,
    primaryConcept,
    secondaryConcept,
    primarySubConcept,
    secondarySubConcept,
    questionUID,
    allLanguages,
    selectedLanguages
  } = data;
  const [toBeRemoved, setToBeRemoved] = useState(
    selectedLanguages[selectedLanguages.length - 1]
  );
  const dispatch = useDispatchToStore();
  return (
    <>
      <Bar />
      {renderIsActive(isActive, questionUID, isEditForm, dispatch)}
      {renderDescriptiveQuestionType(descriptiveQuestionType, dispatch)}
      {renderQuestionDifficulty(difficultyLevel, dispatch)}
      {renderExpectedTimeTaken(expectedTime, dispatch)}
      {renderGoalField(goal, searchedGoals, setSearchedGoals, dispatch)}
      {renderQuestionType(questionType, dispatch)}
      {questionType === QUESTION_TYPE_MAP.INTEGER &&
        renderTypeOfInteger(integerDataType, dispatch)}
      {renderHasPassage(hasPassage, dispatch)}
      {renderLanguageField(
        selectedLanguages,
        allLanguages,
        setShowConfirm,
        setToBeRemoved,
        dispatch
      )}
      {renderPreviousYearQuestion(isPreviousYearQuestion, goal, dispatch)}
      {renderExamField(
        exam,
        searchedExams,
        goal,
        isPreviousYearQuestion,
        setSearchedExams,
        dispatch
      )}
      {renderExamYear(examYear, isPreviousYearQuestion, dispatch)}
      {renderHasDiagram(hasDiagram, dispatch)}
      {renderHasEquation(hasEquation, dispatch)}
      {renderPrimaryConcept(
        primaryConcept,
        searchedConcepts,
        goal,
        setSearchedConcepts,
        dispatch
      )}
      {primaryConcept && (
        <Topology>{`${primaryConcept.parent.name} > ${primaryConcept.name}`}</Topology>
      )}
      {renderPrimarySubConcept(
        primarySubConcept,
        searchedSubConcepts,
        primaryConcept,
        setSearchedSubConcepts,
        dispatch
      )}
      {primarySubConcept && (
        <Topology>{`${primarySubConcept.parent.parent.name} > ${primarySubConcept.parent.name} > ${primarySubConcept.name}`}</Topology>
      )}
      {renderSecondaryConcept(
        secondaryConcept,
        searchedConcepts,
        goal,
        setSearchedConcepts,
        dispatch
      )}
      {secondaryConcept && (
        <Topology>{`${secondaryConcept.parent.name} > ${secondaryConcept.name}`}</Topology>
      )}
      {renderSecondarySubConcept(
        secondarySubConcept,
        searchedSubConcepts,
        secondaryConcept,
        setSearchedSubConcepts,
        dispatch
      )}
      {secondarySubConcept && (
        <Topology>{`${secondarySubConcept.parent.parent.name} > ${secondarySubConcept.parent.name} > ${secondarySubConcept.name}`}</Topology>
      )}
      <Dialog
        onClose={closeModal}
        dialogState={showConfirm}
        heading="Are you sure you want to remove the language?"
        message="This will result in losing any unsaved data for that language"
        acceptLabel="Remove"
        rejectLabel="Cancel"
        acceptOnClick={removeLanguage(
          toBeRemoved,
          setToBeRemoved,
          selectedLanguages,
          dispatch,
          closeModal
        )}
        rejectOnClick={closeModal}
      />
    </>
  );
};

export default Options;

Options.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  isEditForm: PropTypes.bool.isRequired
};
